<template>
  <div>
    <div v-if="usePlaceholder" class="flex justify-center">
      <img
        class="w-3/4"
        src="/static/cloud_empty.png"
        alt="No Words in Word Cloud"
      />
    </div>
    <div v-else class="flex justify-center">
      <VueWordCloud
        style="width: 90%; height: 500px; margin: 0"
        :spacing="0.2"
        :words="wordCloudData"
        :color="
          ([, , sent]) =>
            sent === 'blu' ? '#72CCD7' : sent === 'red' ? '#EB2D7C' : '#273248'
        "
        font-family="Helvetica"
        font-weight="Bold"
      >
        <template slot-scope="{ text, weight }">
          <div
            :title="weight"
            class="cursor-pointer"
            @click="onWordClick(text)"
          >
            {{ text }}
          </div>
        </template>
      </VueWordCloud>
    </div>
    <pl-dialog
      v-if="isModalOpen === true && selectedWord !== null"
      @close="closeModal()"
      :title="
        selectedWord.word + ' (' + selectedWordData.sentences.length + ')'
      "
      :size="'lg'"
      :disableOutsideClick="true"
    >
      <!-- Modal body -->
      <div class="p-7 overflow-y-auto w-full">
        <div
          v-for="(sentences, index) in sentencesByUser"
          class="border px-6 py-6 rounded mb-5"
          :key="index"
        >
          <div class="flex items-center mb-3">
            <img
              class="inline-block h-9 w-9 rounded-full mr-2 ring-2 ring-white"
              :src="getUserAvatar(index)"
              alt="Avatar"
            />
            <h5 class="text-base font-semibold">
              {{ getUserName(index) }}
            </h5>
          </div>
          <div class="px-6 py-6 rounded mb-5">
            <div v-for="(sent, index) in sentences" :key="index">
              <div class="mb-5" v-html="getSentence(sent.sentence)"></div>
              <div v-if="index < sentences.length - 1">...</div>
            </div>
          </div>
          <div class="flex justify-start">
            <button
              class="bg-primary text-white px-5 py-3 rounded"
              @click="openSubmission(index)"
            >
              See in Context
            </button>
          </div>
        </div>
      </div>
    </pl-dialog>
    <pl-dialog
      v-if="see_in_context_modal"
      @close="see_in_context_modal = false"
      :title="'In Context - ' + selectedWord.word"
      :size="'lg'"
      :disableOutsideClick="true"
    >
      <body
        class="flex w-full flex-col overflow-y overflow-x-hidden h-full mb-12"
      >
        <div class="flex items-center mt-5">
          <SubmissionCard
            class="w-full px-12"
            :key="submissionToOpen.id"
            :submission="submissionToOpen"
            :selectedWordData="selectedWordData"
            :studentActivitySummary="true"
            :isInSummary="true"
            :isPlagPanel="is_anonymous_submission"
            :isAnonForPlagPanel="is_anonymous_submission"
            :showVerticalDotMenu="false"
          />
        </div>
      </body>
      <footer class="px-7 py-4">
        <div class="flex items-center justify-between w-full">
          <div class="text-sm">
            <button
              class="mx-5 px-8 py-2 w-24 bg-gray-100 hover:bg-gray-200 rounded cursor-pointer text-gray-400 :hover:text-gray-500 font-semibold disabled:opacity-50"
              @click="see_in_context_modal = false"
            >
              Back
            </button>
          </div>
          <div class="text-sm">
            <button
              @click="seeFullConversation"
              class="px-8 py-2 bg-primary rounded cursor-pointer text-white font-semibold disabled:opacity-50"
            >
              See full conversation
            </button>
          </div>
        </div>
      </footer>
    </pl-dialog>
  </div>
</template>
<script>
  import Vue from 'vue';
  import VueWordCloud from 'vuewordcloud';
  import { EventBus } from 'utils/event-bus';
  import { mapGetters } from 'vuex';

  export default Vue.extend({
    components: {
      [VueWordCloud.name]: VueWordCloud,
      SubmissionCard: () =>
        import(
          'features/onlineRoundtables/components/submissionCard/submissionCard'
        ),
    },
    data() {
      return {
        //isEmptyWords: false,
        usePlaceholder: false,
        wordCloudData: [],
        keywordData: null,
        isModalOpen: false,
        selectedWordData: null,
        submissionToOpen: {},
        sentenceHighlighted: '',
        see_in_context_modal: false,
        is_anonymous_submission: false,
        wordCloudLanguage: '',
      };
    },
    computed: {
      ...mapGetters([
        'getCurrentRT',
        'getProfile',
        'getCourseDetail',
        'hasTeacherPermissionsInCourse',
        'getOrderedSubmissions',
      ]),

      sentencesByUser() {
        var result = this.selectedWordData.sentences.reduce(function (r, a) {
          r[a.userId] = r[a.userId] || [];
          r[a.userId].push(a);
          return r;
        }, Object.create(null));
        return result;
      },
    },
    methods: {
      initializeWordCloud() {
        this.wordCloudData = [];
        this.usePlaceholder = false;
        if (
          this.getCurrentRT &&
          this.getCurrentRT.submissions &&
          this.getCurrentRT.submissions.length
        ) {
          this.$store.dispatch('getRoundtableKeywords').then(() => {
            const submissions_with_content =
              this.getCurrentRT.submissions.filter(
                (s) => s.trimmed_prompt_response
              );
            if (
              !this.getCurrentRT.keywords &&
              submissions_with_content.length
            ) {
              this.$store.dispatch('keywordGenerationForOlderRoundtables');
            }
            if (
              this.getCurrentRT.keywords &&
              this.getCurrentRT.keywords.length
            ) {
              this.usePlaceholder = false;
              try {
                this.keywordData = JSON.parse(this.getCurrentRT.keywords);
                const lang = this.keywordData.l;
                if (typeof lang !== 'string') {
                  let max = 0;
                  let maxLang = 'Does not exist';
                  Object.keys(lang).forEach((language) => {
                    if (lang[language] > max) {
                      max = lang[language];
                      maxLang = language;
                    }
                  });
                  this.wordCloudLanguage =
                    maxLang === 'Does not exist' ? 'English' : maxLang;
                } else {
                  this.wordCloudLanguage = this.keywordData.l;
                }
                this.$emit('cloudLanguague', this.wordCloudLanguage);
                let keys = Object.keys(this.keywordData);
                const len = keys.length > 80 ? 80 : keys.length;
                for (let i = 0; i < len; i++) {
                  const keyword = keys[i];
                  if (this.keywordData[keyword]['removed'] === 'False') {
                    this.wordCloudData.push([
                      keyword,
                      this.keywordData[keyword]['sentences'].length,
                      this.keywordData[keyword]['overall_sentiment'] ===
                      'positive'
                        ? 'blu'
                        : this.keywordData[keyword]['overall_sentiment'] ===
                          'neutral'
                        ? 'dark'
                        : 'red',
                    ]);
                  }
                }
                if (this.wordCloudData.length === 0) {
                  this.usePlaceholder = true;
                }
              } catch (error) {
                console.error(error);
                this.usePlaceholder = true;
              }
            }
          });
        } else {
          this.usePlaceholder = true;
        }
      },
      closeModal: function () {
        this.isModalOpen = false;
      },
      onWordClick(word) {
        this.isModalOpen = true;

        this.selectedWord = { word: word };

        if (this.selectedWordData && word === this.selectedWordData.word) {
          this.showSentences = false;
          this.selectedWordData = null;
        } else {
          this.selectedWordData = Object();
          this.selectedWordData.word = word;
          this.selectedWordData.sentences = this.keywordData[word].sentences;
          this.selectedWordData.sentences.forEach((sent) => {
            const isHidden = this.checkIsSubmissionHidden(sent.userId);
            sent.is_hidden = isHidden;
          });
          this.selectedWordData.freq = this.selectedWordData.sentences.length;
          this.showSentences = true;
        }
      },
      getUserName(userId) {
        return this.getSubmissionValue(userId, 'name');
      },
      getUserAvatar(userId) {
        return this.getSubmissionValue(userId, 'avatar_url');
      },
      getSubmissionValue(userId, parameter) {
        let submissions = this.getCurrentRT.submissions;
        let submission = submissions.find(
          (submission) =>
            submission.user.id === userId ||
            (submission.anonymous_identity_id &&
              submission.anonymous_identity_id === userId)
        );
        if (submission) {
          if (!this.getCurrentRT.enable_annonymous) {
            return submission.user[parameter];
          } else {
            if (
              submission.anonymous_identity &&
              !submission.teacher_submission
            ) {
              return submission.anonymous_identity[parameter];
            }
            return submission.user[parameter];
          }
        }
      },
      checkIsSubmissionHidden(userID) {
        let hidden = false;
        if (this.getCurrentRT.submissions[0].user) {
          this.getCurrentRT.submissions.forEach((submission) => {
            if (
              submission.user.id === userID ||
              (submission.anonymous_identity_id &&
                submission.anonymous_identity_id === userID)
            ) {
              hidden = submission.is_hidden;
            }
          });
        }
        return hidden;
      },
      getSentence(sentence) {
        sentence = sentence.replace(
          new RegExp(this.selectedWordData.word, 'ig'),
          '<span style="background-color: #F596BD">' +
            this.selectedWordData.word +
            '</span>'
        );

        return sentence.replace(
          new RegExp(this.selectedWordData.word.toLowerCase(), 'ig'),
          '<span style="background-color: #F596BD">' +
            this.selectedWordData.word +
            '</span>'
        );
      },
      openSubmission(userID) {
        let id = '';
        if (this.getCurrentRT.submissions[0].user) {
          this.getCurrentRT.submissions.forEach((submission) => {
            if (
              submission.user.id === userID ||
              (submission.anonymous_identity_id &&
                submission.anonymous_identity_id === userID)
            ) {
              if (!submission.teacher_submission) {
                this.is_anonymous_submission = true;
              }
              id = submission.id;
              this.submissionToOpen = submission;
            }
          });
        } else {
          this.getCurrentRT.submissions.forEach((submission) => {
            if (submission.anonymous_identity.id === userID) {
              if (!submission.teacher_submission) {
                this.is_anonymous_submission = true;
              }
              id = submission.id;
              this.submissionToOpen = submission;
            }
          });
        }

        this.$store.dispatch('submissionClicked', id).then((data) => {
          this.submissionToOpen.comments = data.comments;
          this.submissionToOpen.opened_submission = false;
          this.see_in_context_modal = true;
        });
        this.$store.dispatch('loadSubmission', id);
      },
      seeFullConversation() {
        const route = {
          name: 'roundtable.index',
          params: {
            courseID: this.$route.params.courseID,
            roundtableID: this.$route.params.roundtableID,
          },
          query: {
            submissionID: this.submissionToOpen.id,
            replyCommentID: '',
            topLevelCommentID: '',
          },
        };

        this.$store.commit('setIsFromNotification', true);
        this.see_in_context_modal = false;
        this.closeModal();
        route.name = 'roundtable.discussion';
        Vue.router.push(route);
        this.$store.commit('setIsReloadComponent');
      },
    },
    async mounted() {
      await this.initializeWordCloud();
      this.keywordData = this.getCurrentRT.keywords;
      this.usePlaceholder = true;

      EventBus.$on('updateWordList', (keywords) => {
        this.keywordData = keywords;
        this.wordCloudData = [];

        let keys = Object.keys(this.keywordData);
        const len = keys.length > 80 ? 80 : keys.length;
        for (let i = 0; i < len; i++) {
          const keyword = keys[i];
          if (this.keywordData[keyword]['removed'] === 'False') {
            this.wordCloudData.push([
              keyword,
              this.keywordData[keyword]['sentences'].length,
              this.keywordData[keyword]['overall_sentiment'] === 'positive'
                ? 'blu'
                : this.keywordData[keyword]['overall_sentiment'] === 'neutral'
                ? 'dark'
                : 'red',
            ]);
          }
        }
      });
    },
  });
</script>



// WEBPACK FOOTER //
// KeyWordCloud.vue?f0e7da5a